// MIA Chat Mock Mode Toggle Utility
// This file provides console commands for quickly toggling mock mode

import { 
  isMockModeEnabled, 
  updateMockMode, 
  updateMockDelay,
  getMockDelay,
  miaChatConfig 
} from './config';

// Make functions available globally for console access
declare global {
  interface Window {
    miaChatMock: {
      enable: () => void;
      disable: () => void;
      toggle: () => void;
      status: () => void;
      setDelay: (delay: number) => void;
      config: () => void;
    };
  }
}

// Console utility functions
const enableMockMode = () => {
  updateMockMode(true);
  console.log('✅ MIA Chat Mock Mode ENABLED');
  console.log('🔴 All API calls will now use mock data');
};

const disableMockMode = () => {
  updateMockMode(false);
  console.log('✅ MIA Chat Mock Mode DISABLED');
  console.log('🟢 All API calls will now use real API endpoints');
};

const toggleMockMode = () => {
  const currentMode = isMockModeEnabled();
  updateMockMode(!currentMode);
  console.log(`🔄 MIA Chat Mock Mode ${!currentMode ? 'ENABLED' : 'DISABLED'}`);
};

const showStatus = () => {
  const enabled = isMockModeEnabled();
  const delay = getMockDelay();
  console.log('📊 MIA Chat Mock Mode Status:');
  console.log(`   Mode: ${enabled ? '🔴 MOCK' : '🟢 REAL API'}`);
  console.log(`   Delay: ${delay}ms`);
  console.log(`   Environment: ${process.env.NODE_ENV || 'development'}`);
  console.log(`   API URL: ${miaChatConfig.api.baseUrl}`);
};

const setDelay = (delay: number) => {
  updateMockDelay(delay);
  console.log(`⏱️  Mock delay set to ${delay}ms`);
};

const showConfig = () => {
  console.log('⚙️  MIA Chat Configuration:', miaChatConfig);
};

// Initialize global console commands
const initializeConsoleCommands = () => {
  if (typeof window !== 'undefined') {
    window.miaChatMock = {
      enable: enableMockMode,
      disable: disableMockMode,
      toggle: toggleMockMode,
      status: showStatus,
      setDelay: setDelay,
      config: showConfig,
    };

    // Log available commands
    console.log('🚀 MIA Chat Mock Mode Console Commands Available:');
    console.log('   miaChatMock.enable()    - Enable mock mode');
    console.log('   miaChatMock.disable()   - Disable mock mode');
    console.log('   miaChatMock.toggle()    - Toggle mock mode');
    console.log('   miaChatMock.status()    - Show current status');
    console.log('   miaChatMock.setDelay(n) - Set mock delay to n ms');
    console.log('   miaChatMock.config()    - Show full configuration');
    console.log('');
    console.log('💡 Example: miaChatMock.toggle() to switch modes');
  }
};

// Auto-initialize when imported
initializeConsoleCommands();

// Export functions for programmatic use
export {
  enableMockMode,
  disableMockMode,
  toggleMockMode,
  showStatus,
  setDelay,
  showConfig,
  initializeConsoleCommands,
};
